import defer from 'lodash/defer';
import includes from 'lodash/includes';
import findIndex from 'lodash/findIndex';
import first from 'lodash/first';
import loglevel from 'loglevel';

import Backbone from 'backbone';

import {
  setupAppContainerLayoutClassName
} from 'zoomforth_codesbane_viewer/lib/setup_utils.js';

import {
  getCurrentSubpage,
  getMenuItemById,
  getOrderedAndSelectedNonEmptyContentItemsForSection,
  getOrderedContentUuidsForSection,
  getRenderAsMobile,
  getSectionSelectedContent,
  getSectionSelectedContentUuid,
  getIsCurrentSubpageFullPageSection,
  getSectionForContent,
  getSectionById,
  getViewerType
} from 'zoomforth_codesbane_viewer/reducers/index.js';

import { 
  executeMatchedRouteIfSameHashOnMenuItemClick 
} from 'zoomforth_codesbane_viewer/lib/backbone_history_utils.js';

import {
  LINK_TYPE_INTERNAL
} from 'zoomforth_codesbane_viewer/const/link_types.js';

import {
  isSingletonContentSection,
  isOrderedContentSection,
  isGridPositionsContentSection
} from 'zoomforth_codesbane_viewer/lib/sections.js';

import {
  SECTION_TYPE_ACCORDION
} from 'zoomforth_codesbane_viewer/const/sections.js';

import { getATagProps as getATagPropsForAppAction } from 'zoomforth_codesbane_viewer/lib/app_actions.js';

import { 
  getTracker,
  getRouter 
} from 'zoomforth_codesbane_viewer/lib/global_store.js';

import { getUuidFrom } from 'zoomforth_codesbane_viewer/lib/id_utils.js';

import { 
  canOpenOverlayFor,
  getContentItemType
} from 'zoomforth_codesbane_viewer/lib/page_content_utils.js';
import { getInternalLinkUrl } from 'zoomforth_codesbane_viewer/lib/app_actions.js';

import {
  getContentItemByUuid,
  getSubpageForContent
} from 'zoomforth_codesbane_viewer/reducers/index.js';

import {
  SET_ACCORDION_STATE_BY_SECTION_ID,
  SET_SHOULD_RENDER_AS_MOBILE,
  SET_IN_PAGE_SEARCH_VALUE,
  SET_IS_IN_PAGE_SEARCH_DISPLAYED,
  SET_IS_NEEDS_REFRESH_DISPLAYED,
  SET_SECTION_SELECTED_CONTENT,
  SET_APPLIED_TAG_IDS_BY_CATEGORY_ID_FOR_GRID_SECTION_FILTER,
  SET_ACTIVE_TAB_ID
} from '../const/action_types.js';

export const setCurrentSubpageId = (id)=> {
  return {
    type: 'SET_CURRENT_SUBPAGE',
    id: id
  };
};

export const setLastClickedSubmenuItemId = (id)=> {
  return {
    type: 'SET_LAST_CLICKED_SUBMENU_ITEM_ID',
    id: id
  };
};

export const setLastClickedToplevelInternalLinkId = (id)=> {
  return {
    type: 'SET_LAST_CLICKED_TOPLEVEL_INTERNAL_LINK_ID',
    id: id
  };
};
  
export const setAllowAutoplayForLinkedToContent = (contentUuid)=>
  (dispatch, getState)=> {

    const contentItem = getContentItemByUuid(getState(), contentUuid);
    const linkedToContentUuid = contentItem.tile_action_data.contentUuid;

    const section = getSectionForContent(getState(), linkedToContentUuid);

    dispatch({
      type: 'SET_ALLOW_AUTOPLAY_FOR_CONTENT',
      sectionId: section.id,
      contentUuid: linkedToContentUuid
    });
  };

export const selectSectionContent = (section, contentItemOrUuid, allowAutoplay=false)=> {
  if (!includes(['videoplaylist', 'slideshow', SECTION_TYPE_ACCORDION], section.type)) {
    throw new Error(`Cannot select content in section of type ${section.type}`);
  }
  const contentUuid = getUuidFrom(contentItemOrUuid);
  return {
    type: SET_SECTION_SELECTED_CONTENT,
    section,
    contentUuid,
    allowAutoplay
  };
};

/*
  Select the next item in the video playlist section. If the last item is currently 
  selected this is a noop 
*/
export const selectNextContentInVideoPlaylistSection = (section, allowAutoplay=false)=>
  (dispatch, getState)=> {
    const [contentItems, selectedContentItem] = getOrderedAndSelectedNonEmptyContentItemsForSection(getState(), section);
    const index = findIndex(contentItems, ['uuid', selectedContentItem.uuid]);
    const indexOfNextContent = index + 1;
    if (indexOfNextContent == contentItems.length) {
      return; // End of list
    }
    dispatch(selectSectionContent(section, contentItems[indexOfNextContent], allowAutoplay));
  };


export const setIsInPageSearchDisplayed = (isInPageSearchDisplayed)=> {
  return {
    type: SET_IS_IN_PAGE_SEARCH_DISPLAYED,
    isInPageSearchDisplayed
  };
};

export const setIsNeedsRefreshDisplayed = (isNeedsRefreshDisplayed)=> {
  return {
    type: SET_IS_NEEDS_REFRESH_DISPLAYED,
    isNeedsRefreshDisplayed
  };
};


export const setInPageSearch = (value)=> {
  return {
    type: SET_IN_PAGE_SEARCH_VALUE,
    value
  };
};


export const setOverlayContentUuid = (contentUuid, { isOverlayOpenedFromSlideshow = false } = {})=> {
  return {
    type: 'SET_ACTIVE_OVERLAY_CONTENT',
    contentUuid,
    isOverlayOpenedFromSlideshow
  };
};

export const goToSubpageAndContentWithUuid = (contentUuid)=>
  (dispatch, getState)=> {
    const pageContentItem = getContentItemByUuid(getState(), contentUuid);

    const subpage = getSubpageForContent(getState(), pageContentItem);
    dispatch(setCurrentSubpageId(subpage.id));
    dispatch(goToContent(pageContentItem));
  };

export const goToContent = (contentItem, { animationTimeMs } = {})=>
  (dispatch, getState)=> {

    const sectionForContent = getSectionForContent(getState(), contentItem);

    if (!sectionForContent) {
      throw new Error('Missing section for specified content; Cannot goto content.');
    }

    if (isSingletonContentSection(sectionForContent)) {
      dispatch(scrollToSection(sectionForContent, { animationTimeMs }));

    } else if (isOrderedContentSection(sectionForContent)) {
      if (sectionForContent.type === 'accordion') {
        dispatch(scrollToAccordionItem(contentItem, { animationTimeMs }));
        dispatch(selectSectionContent(sectionForContent, contentItem));
      } else {
        dispatch(scrollToSection(sectionForContent, { animationTimeMs }));
        dispatch(selectSectionContent(sectionForContent, contentItem));
      }

    } else if (isGridPositionsContentSection(sectionForContent)) {
      dispatch(scrollToTile(contentItem, { animationTimeMs }));

    } else {
      loglevel.error('Cannot go to content. It is in an unexpected section type');
    }

  };

export const simulateInternalLinkClickTargetedAtContent = (contentUuid)=>
  (dispatch, getState)=> {

    const pageContentItem = getContentItemByUuid(getState(), contentUuid);
    const section = getSectionForContent(getState(), pageContentItem);
    const subpage = getSubpageForContent(getState(), pageContentItem);

    const shouldOpenOverlay = (
      section.type === 'tiles' && (
        // we almost always want to show the overlay except
        // for image tiles because it "feels" wrong for search results
        canOpenOverlayFor(section, pageContentItem) && 
        getContentItemType(pageContentItem) !== 'photo'
      )
    );

    const internaLink = getInternalLinkUrl({
      sectionId: section.id,
      subpageId: subpage.id,
      contentUuid,
      shouldOpenOverlay
    });

    window.location.hash = internaLink;
  };


export const simulateMenuItemClickForMenuItemId = (menuItemId)=>
  (dispatch, getState)=> {
    const menuItem = getMenuItemById(getState(), menuItemId);
    const currentSubpage = getCurrentSubpage(getState());
    const isRenderedAsMobile = getRenderAsMobile(getState());
    dispatch(simulateMenuItemClick(
      menuItem, 
      currentSubpage, 
      { shouldScrollTopForPageChange: isRenderedAsMobile }
    ));
  };


export const simulateMenuItemClick = (menuItem, currentSubpage, { shouldScrollTopForPageChange=false })=> {
  const { href, target } = getATagPropsForAppAction(menuItem.appAction);
  if (target) {
    window.open(href, target);
  } else {
    window.location = href;
  }
  return handleMenuItemPostClickBehavior(menuItem, currentSubpage, { shouldScrollTopForPageChange });
};


export const handleMenuItemPostClickBehavior = (menuItem, currentSubpage, { shouldScrollTopForPageChange=false }={})=>
  (dispatch)=> {
    // These actions take place in addition to those implicit on an `onClick` handler on an a tag
    getTracker().handleNavMenuItemClick(menuItem);
    if (
      menuItem.appAction.type === LINK_TYPE_INTERNAL &&
      currentSubpage.id === menuItem.appAction.subpageId &&
      !menuItem.appAction.contentUuid &&
      !menuItem.appAction.sectionId
    ) {
      // Clicking on the *current* subpage's menu item should scroll you to the top
      // as long as we dont also need to scroll somewhere else
      dispatch(scrollToTopAndTrack());        
    
    } else if (
      shouldScrollTopForPageChange &&
      menuItem.appAction.type === LINK_TYPE_INTERNAL &&
      menuItem.appAction.subpageId &&
      !menuItem.appAction.contentUuid &&
      !menuItem.appAction.sectionId
    ) {
      // Tapping on a subpage should scroll you to the top immediately
      dispatch(scrollToTopAndTrack(/* animationTimeMs = */0));

    } else if (menuItem.appAction.type === LINK_TYPE_INTERNAL) {
      dispatch(setLastClickedToplevelInternalLinkId(menuItem.id));
    
    }
    executeMatchedRouteIfSameHashOnMenuItemClick(menuItem);
  };

export const scrollToSection = (section, { animationTimeMs } = {})=> {
  return {
    type: 'SET_SCROLLING_TO_SECTION',
    section,
    animationTimeMs
  };
};

export const doneScrollingToSection = ()=> {
  return {
    type: 'SET_SCROLLING_TO_SECTION',
    section: null
  };
};

/**
  Immediately set page scroll to top.
  Do not track in analytics.
*/
export const setScrollToTop = ()=>
  (dispatch)=> {
    dispatch(scrollToTop(0));
  };

/**
  Scroll the page to the top with an animation.
*/
export const scrollToTop = (animationTimeMs=500)=>
  (dispatch)=> {
    dispatch({
      type: 'SET_SCROLLING_TO_TOP',
      shouldScrollToTop: true,
      animationTimeMs
    });
  };

/**
  Scroll the page to the top with an animation.
  Track this in analytics.
*/
export const scrollToTopAndTrack = (animationTimeMs=500)=>
  (dispatch)=> {
    dispatch(scrollToTop(animationTimeMs));
    getTracker().handleContentScrolledTop();
  };

export const doneScrollingToTop = ()=> {
  return {
    type: 'SET_SCROLLING_TO_TOP',
    shouldScrollToTop: false
  };
};

export const scrollToTile = (contentItemOrUuid, { animationTimeMs } = {})=> {
  return {
    type: 'SET_SCROLLING_TO_TILE',
    contentItemOrUuid,
    animationTimeMs
  };
};

export const scrollToAccordionItem = (contentItemOrUuid, { animationTimeMs } = {})=> {
  return {
    type: 'SET_SCROLLING_TO_ACCORDION_ITEM',
    contentItemOrUuid,
    animationTimeMs
  };
};

export const doneScrollingToContentItem = ()=> {
  return {
    type: 'SET_SCROLLING_TO_TILE',
    contentItemOrUuid: null
  };

};

export const syncToUrlSubpageAndContent = (sectionId)=>
  (dispatch, getState) => {

    const currentSubpage = getCurrentSubpage(getState());
    const selectedContentItem = getSectionSelectedContent(getState(), sectionId);

    if (currentSubpage && selectedContentItem) {
      defer(()=> { 
        // In case we arrived here via the router handling a page load, we need to
        // defer to give the router a chance to finish up before we navigate again
        getRouter().navigateToSubpageAndContent(
          currentSubpage,
          selectedContentItem.uuid,
          {
            trigger: false,
            replace: true
          }
        );        
      });

    }
  };

export const initializeCurrentSubpage = ()=>
  (dispatch, getState)=> {
    const currentSubpage = getCurrentSubpage(getState());
    dispatch(setCurrentSubpageId(currentSubpage.id));
  };

export const submitForm = ({ responseData, pageId, formId, contentUuid, onSuccess, onError })=>
  (dispatch, getState)=> {

    if (getViewerType(getState()) !== 'live') {
      alert('Did not record form response because this is not a live page.');
      return;
    }

    const FormResponse = Backbone.Model.extend({
      urlRoot: '/_rpc/forms/submit_response'
    });
    const responseModel = new FormResponse({
      user_response: responseData,
      page_id: pageId,
      form_id: formId
    });
    
    // handles undefined callbacks safely
    responseModel.save(null, { success: onSuccess, error: onError });
    getTracker().handleFormSubmit(contentUuid);
  };

export const handleSlideshowSlideDisplayTracking = (section, contentItem)=>
  (dispatch, getState)=> {
    // getSectionSelectedContentUuid will only be set if a content item is
    // selected. Thus, we only want to fire tracking of a slide if the user has
    // actually selected it.
    const shouldTrackSlideDisplay = !!getSectionSelectedContentUuid(getState(), section.id);

    if (shouldTrackSlideDisplay) {
      getTracker().handleSlideshowItemSelect(contentItem);
    }
  };

export const handleSubpageDisplayed = ()=>
  (dispatch, getState)=> {
    const currentSubpage = getCurrentSubpage(getState());
    const isCurrentSubpageFullPage = getIsCurrentSubpageFullPageSection(getState());

    // if the current subpage is a multisection subpage we fire a generic tracking event
    if (!isCurrentSubpageFullPage) {
      getTracker().handleSubpageDisplayed(currentSubpage);
      return;
    }

    const fullpageSection = getSectionById(getState(), first(currentSubpage.orderedSectionIds));
    const fullpageSectionSelectedContent = getSectionSelectedContent(getState(), fullpageSection);
    const hasDisplayableContent = !fullpageSection.isHidden && !!fullpageSectionSelectedContent;

    if (hasDisplayableContent) {
      if (fullpageSection.type === 'pdf') {
        getTracker().handlePdfSubpageDisplayed(fullpageSectionSelectedContent);

      } else if (fullpageSection.type === 'videoplaylist') {
        getTracker().handleVideoPlaylistSubpageDisplayed(fullpageSectionSelectedContent);
        dispatch(syncToUrlSubpageAndContent(fullpageSection.id));

      } else if (fullpageSection.type === 'slideshow') {
        getTracker().handleSlideshowSubpageDisplayed(fullpageSectionSelectedContent);
        dispatch(syncToUrlSubpageAndContent(fullpageSection.id));
      }     
    }
  };

export const setActiveTabId = (sectionId, tabId) => {
  return {
    type: SET_ACTIVE_TAB_ID,
    sectionId,
    tabId
  };
};

export const setAppliedTagIdsByCategoryIdForGridSection = (sectionId, tagIdsByCategoryId)=> {
  return { type: SET_APPLIED_TAG_IDS_BY_CATEGORY_ID_FOR_GRID_SECTION_FILTER, sectionId, tagIdsByCategoryId };
};

export const setShouldRenderAsMobile = (shouldRenderAsMobile)=> {
  setupAppContainerLayoutClassName(shouldRenderAsMobile);

  return { type: SET_SHOULD_RENDER_AS_MOBILE, shouldRenderAsMobile };
};

export const handleSearchResultClick = (ident)=>
  (dispatch, getState)=> {
    const state = getState();

    const [type, idOrUuid] = ident.split('::');

    loglevel.info('Chose item: ', ident);

    if (type == 'pageContentItem') {
      dispatch(simulateInternalLinkClickTargetedAtContent(idOrUuid));

      const contentItem = getContentItemByUuid(state, idOrUuid);
      const section = getSectionForContent(state, contentItem);

      if (section.type === 'accordion') {
        const idx = findIndex(
          getOrderedContentUuidsForSection(state, section),
          (uuid)=> uuid === contentItem.uuid
        );

        dispatch(setOpenIndicesForAccordion(section.id, [idx]));
      }
    }

    if (type == 'menuItem') {
      dispatch(simulateMenuItemClickForMenuItemId(idOrUuid));
    }
  };

export const setOpenIndicesForAccordion = (sectionId, openIndices)=>
  (dispatch, getState)=> {
    const state = getState();
    const section = getSectionById(state, sectionId);

    dispatch({
      type: SET_ACCORDION_STATE_BY_SECTION_ID,
      sectionId: section.id,
      openIndices: openIndices
    });
  };
